import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, Play } from "lucide-react";
import { useChat } from "@/lib/chat";
import { api, errMsg, getToken } from "@/lib/api";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { analytics } from "@/lib/analytics";

const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round((n || 0) / 1024)} KB`);

export const Backups = () => {
  const { me } = useChat();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/backups").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backups/run");
      toast[data.started ? "success" : "info"](data.message);
      analytics.track("admin_backup_run", { category: "admin", target: "backup" });
      setTimeout(load, 2500); setTimeout(load, 8000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const download = async (b) => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/${b.id}/download`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = b.path.split("/").pop();
      a.click();
      URL.revokeObjectURL(a.href);
      analytics.track("admin_backup_downloaded", { category: "admin", target: "backup", target_id: b.id });
    } catch (e) { toast.error(`Download failed: ${e.message}`); }
  };
  const last = d?.backups.find((b) => b.status === "ok");
  return (
    <div data-testid="health-backups">
      <div className="tc-stats tc-stats--fit">
        <div className="tc-stat"><div className="tc-stat__label">Last good backup</div><div className="tc-stat__value" data-testid="backup-last">{last ? fmtRelative(last.started_at) : "—"}</div><div className="tc-stat__sub">{last ? `${last.documents.toLocaleString()} documents · ${kb(last.size)}` : "No successful backup yet"}</div></div>
        <div className="tc-stat"><div className="tc-stat__label">Schedule</div><div className="tc-stat__value">Nightly</div><div className="tc-stat__sub">{d?.schedule || "Daily at 03:30 UTC"} · keeps the last {d?.keep ?? 14} nightly + {d?.manual_keep ?? 5} manual</div></div>
        <div className="tc-stat"><div className="tc-stat__label">What's included</div><div className="tc-stat__value">{d?.collections.length ?? "—"}</div><div className="tc-stat__sub">collections — members, channels, every message & file record, analytics, settings</div></div>
        <div className="tc-stat" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {me?.is_admin ? <button className="tc-btn tc-btn--primary" disabled={busy || d?.running} onClick={run} data-testid="backup-run-now"><Play size={14} /> {d?.running ? "Backup running…" : "Back up now"}</button> : <span className="muted">Admins can start a backup on demand.</span>}
        </div>
      </div>
      <div className="tc-admin-card">
        <div className="tc-admin-card__head"><DatabaseBackup size={16} /> Backup history <span className="right">{d?.backups.length ?? 0}</span></div>
        <div style={{ overflowX: "auto" }}>
          <table className="tc-table" data-testid="backups-table">
            <thead><tr><th>When</th><th>Trigger</th><th>Status</th><th>Documents</th><th>Size</th><th>Took</th><th /></tr></thead>
            <tbody>
              {(d?.backups || []).map((b) => (
                <tr key={b.id} data-testid={`backup-row-${b.id}`}>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(b.started_at)}</td>
                  <td><span className="tc-pill">{b.trigger}</span></td>
                  <td>{b.status === "ok" ? <span className="tc-pill client">OK</span> : b.status === "running" ? <span className="tc-pill">Running…</span> : <span className="tc-pill danger" title={b.error || ""}>Failed</span>}{b.error && <div className="muted" style={{ fontSize: 11 }}>{b.error.slice(0, 80)}</div>}</td>
                  <td>{b.documents ? b.documents.toLocaleString() : "—"}</td>
                  <td>{b.size ? kb(b.size) : "—"}</td>
                  <td className="muted">{b.duration_ms != null ? `${(b.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                  <td style={{ textAlign: "right" }}>{b.status === "ok" && me?.is_admin && <button className="tc-btn tc-btn--ghost sm" onClick={() => download(b)} data-testid={`backup-download-${b.id}`}><Download size={13} /> Download</button>}</td>
                </tr>
              ))}
              {d && !d.backups.length && <tr><td colSpan={7} className="muted" style={{ textAlign: "center", padding: 24 }}>No backups yet — the first nightly run happens at 03:30 UTC, or start one now.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12 }}>Backups are gzip'd JSON-lines files (one document per line, with the collection name) stored in Total Chat's object storage — readable by any tool and restorable into MongoDB with a short script. Uploaded files themselves already live in object storage; the backup records their metadata and paths.</p>
    </div>
  );
};
